/**
 * Agent Chat Context
 *
 * Enhanced context for managing agent chat sessions with:
 * - WebSocket connection to agents
 * - Claude JSON stream parsing into structured messages
 * - Multi-agent session support
 * - Stats and permission handling
 *
 * Adapted from V1 SessionsContext for V2's agent-based model.
 *
 * @module contexts/AgentChatContext
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useMessages } from './MessagesContext.jsx';
import { useUI } from './UIContext.jsx';
import { useAuth } from './AuthContext.jsx';

// ============================================
// Constants
// ============================================

const WS_MESSAGE_TYPES = {
  // Client -> Server
  OBSERVE_AGENT: 'observe_agent',
  STOP_OBSERVE: 'stop_observe',
  AGENT_MESSAGE: 'agent_message',
  PERMISSION_RESPONSE: 'permission_response',
  SET_PERMISSION_MODE: 'set_permission_mode',
  SET_MODEL: 'set_model',
  STOP_GENERATION: 'stop_generation',
  PING: 'ping',

  // Server -> Client
  AGENT_OUTPUT: 'agent_output',
  AGENT_ERROR: 'agent_error',
  AGENT_STATUS: 'agent_status',
  PERMISSION_REQUEST: 'permission_request',
  OBSERVATION_STARTED: 'observation_started',
  OBSERVATION_STOPPED: 'observation_stopped',
  PERMISSION_MODE_CHANGED: 'permission_mode_changed',
  MODEL_CHANGED: 'model_changed',
  CONTROL_INITIALIZED: 'control_initialized',
  ERROR: 'error',
  PONG: 'pong',
};

// ============================================
// Helpers
// ============================================

function getWebSocketUrl() {
  if (typeof window === 'undefined') {
    return 'ws://localhost:3001/ws';
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;

  // Determine backend host:
  // - Local dev (localhost:5173) → direct connect to backend on :3001
  // - Remote dev (dev.agents.sneakercloud.de) → use Vite proxy (same host)
  // - Production → use nginx proxy (same host)
  let backendHost;
  if (import.meta.env.DEV && host.includes('localhost')) {
    // Local development without proxy
    backendHost = host.replace(/:\d+$/, ':3001');
  } else {
    // Use current host - Vite or nginx will proxy to backend
    backendHost = host;
  }

  return `${protocol}//${backendHost}/ws`;
}

function createAgentSessionState(agentId) {
  return {
    agentId,
    connected: false,
    active: false,
    isProcessing: false,
    error: null,
    stats: {
      totalCost: 0,
      inputTokens: 0,
      outputTokens: 0,
      cacheReadTokens: 0,
      cacheCreationTokens: 0,
      numTurns: 0,
      contextLeftPercent: null,
      tokensUsed: 0,
      contextWindow: 200000,
      isCompacting: false,
    },
    permissionMode: 'default',
    currentModel: null,
    controlInitialized: false,
    availableCommands: [],
    pendingPermission: null,
    unreadCount: 0,
    currentContext: null,
    sessionInfo: null,
  };
}

// ============================================
// Context
// ============================================

const AgentChatContext = createContext(null);

export function AgentChatProvider({ children }) {
  // All agent sessions keyed by agent ID
  const [agentSessions, setAgentSessions] = useState({});

  // WebSocket connection state - MUST be reactive for SessionsView to restore agents on reconnect
  const [wsConnected, setWsConnected] = useState(false);

  // Get dependencies from other contexts
  const {
    addMessage,
    updateLastAssistantMessage,
    setMessages,
    removeSessionMessages,
    getCurrentAssistantMessage,
    setCurrentAssistantMessage,
  } = useMessages();

  const { addTab, removeTab, focusedAgentId, splitSessions } = useUI();
  const { user } = useAuth();

  // WebSocket ref (single connection for all agents)
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const isComponentMounted = useRef(true);

  // Ref to current sessions state (for stable callbacks)
  const sessionsRef = useRef(agentSessions);
  sessionsRef.current = agentSessions;

  // Observed agents set - tracks currently observed agents during session
  // Note: No longer persisted to localStorage - WorkspaceSession is the source of truth
  // UIContext restores agents from WorkspaceSession on page reload
  const observedAgents = useRef(new Set());

  // Ref to store loadHistory function for use in message handler
  const loadHistoryRef = useRef(null);

  // Version counter for loadHistory calls - prevents stale responses from overwriting current state
  const historyLoadVersionRef = useRef({});

  // ----------------------------------------
  // Session State Management
  // ----------------------------------------

  const updateAgentSession = useCallback((agentId, updates) => {
    setAgentSessions(prev => {
      if (!prev[agentId]) {
        // Create session if it doesn't exist
        return {
          ...prev,
          [agentId]: {
            ...createAgentSessionState(agentId),
            ...(typeof updates === 'function' ? updates(createAgentSessionState(agentId)) : updates),
          },
        };
      }
      const newUpdates = typeof updates === 'function' ? updates(prev[agentId]) : updates;
      return {
        ...prev,
        [agentId]: {
          ...prev[agentId],
          ...newUpdates,
        },
      };
    });
  }, []);

  // ----------------------------------------
  // Claude Event Parsing (from JSON stream)
  // ----------------------------------------

  const handleClaudeEvent = useCallback((agentId, event) => {
    if (!event) return;

    const { type } = event;

    switch (type) {
      case 'assistant': {
        const message = event.message;
        if (!message?.content) break;

        const textBlocks = [];
        const toolUseBlocks = [];

        for (const block of message.content) {
          if (block.type === 'text') {
            textBlocks.push(block.text);
          } else if (block.type === 'tool_use') {
            toolUseBlocks.push({
              type: 'tool_use',
              tool: block.name,
              input: block.input,
              toolUseId: block.id,
            });
          }
        }

        if (textBlocks.length > 0) {
          if (getCurrentAssistantMessage(agentId) !== undefined) {
            updateLastAssistantMessage(agentId, textBlocks.join('\n'));
            setCurrentAssistantMessage(agentId, undefined);
          } else {
            addMessage(agentId, {
              type: 'assistant',
              content: textBlocks.join('\n'),
            });
          }
        }

        for (const toolMsg of toolUseBlocks) {
          addMessage(agentId, toolMsg);
        }

        const usage = message.usage;
        if (usage) {
          const inputTokens = (usage.input_tokens || 0) + (usage.cache_read_input_tokens || 0);
          const outputTokens = usage.output_tokens || 0;
          const cacheReadTokens = usage.cache_read_input_tokens || 0;
          const cacheCreationTokens = usage.cache_creation_input_tokens || 0;

          updateAgentSession(agentId, (session) => ({
            stats: {
              ...(session.stats || {}),
              inputTokens: (session.stats?.inputTokens || 0) + inputTokens,
              outputTokens: (session.stats?.outputTokens || 0) + outputTokens,
              cacheReadTokens: (session.stats?.cacheReadTokens || 0) + cacheReadTokens,
              cacheCreationTokens: (session.stats?.cacheCreationTokens || 0) + cacheCreationTokens,
              numTurns: (session.stats?.numTurns || 0) + 1,
            },
          }));
        }
        break;
      }

      case 'user': {
        const message = event.message;
        if (!message?.content) break;

        for (const block of message.content) {
          if (block.type === 'tool_result') {
            addMessage(agentId, {
              type: 'tool_result',
              toolUseId: block.tool_use_id,
              content: typeof block.content === 'string' ? block.content : JSON.stringify(block.content),
              isError: block.is_error || false,
            });
          }
        }
        break;
      }

      case 'content_block_delta':
      case 'stream_event': {
        const delta = event.delta || event;
        if (delta?.type === 'text_delta' || delta?.text) {
          const text = delta.text || '';

          if (getCurrentAssistantMessage(agentId) === undefined) {
            setCurrentAssistantMessage(agentId, text);
            addMessage(agentId, { type: 'assistant', content: text });
          } else {
            const current = getCurrentAssistantMessage(agentId) + text;
            setCurrentAssistantMessage(agentId, current);
            updateLastAssistantMessage(agentId, current);
          }
        }
        break;
      }

      case 'content_block_start': {
        const contentBlock = event.content_block;
        if (contentBlock?.type === 'text') {
          setCurrentAssistantMessage(agentId, '');
          addMessage(agentId, { type: 'assistant', content: '' });
        }
        break;
      }

      case 'content_block_stop': {
        if (getCurrentAssistantMessage(agentId) !== undefined) {
          setCurrentAssistantMessage(agentId, undefined);
        }
        break;
      }

      case 'message_start': {
        updateAgentSession(agentId, { isProcessing: true });
        break;
      }

      case 'message_stop': {
        if (getCurrentAssistantMessage(agentId) !== undefined) {
          setCurrentAssistantMessage(agentId, undefined);
        }
        break;
      }

      case 'result': {
        updateAgentSession(agentId, (session) => ({
          isProcessing: false,
          stats: {
            ...(session.stats || {}),
            totalCost: event.totalCost ?? session.stats?.totalCost ?? 0,
            inputTokens: event.inputTokens ?? session.stats?.inputTokens ?? 0,
            outputTokens: event.outputTokens ?? session.stats?.outputTokens ?? 0,
            cacheReadTokens: event.cacheReadTokens ?? session.stats?.cacheReadTokens ?? 0,
            cacheCreationTokens: event.cacheCreationTokens ?? session.stats?.cacheCreationTokens ?? 0,
            numTurns: event.numTurns ?? session.stats?.numTurns ?? 0,
          },
          currentContext: event.currentContext || session.currentContext,
        }));
        break;
      }

      case 'system': {
        if (event.message) {
          const contextMatch = event.message.match(/Context left[^:]*:\s*(\d+)%/i);
          if (contextMatch) {
            const contextLeft = parseInt(contextMatch[1], 10);
            updateAgentSession(agentId, (session) => ({
              stats: {
                ...(session.stats || {}),
                contextLeftPercent: contextLeft,
              },
            }));
          }
        }
        break;
      }

      default:
        console.log(`[${agentId}] Unhandled claude event:`, type, event);
    }
  }, [addMessage, updateLastAssistantMessage, updateAgentSession, getCurrentAssistantMessage, setCurrentAssistantMessage]);

  // ----------------------------------------
  // WebSocket Message Handling
  // ----------------------------------------

  const handleWsMessage = useCallback((event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    const agentId = data.agentId;

    switch (data.type) {
      case WS_MESSAGE_TYPES.OBSERVATION_STARTED:
        updateAgentSession(agentId, {
          connected: true,
          active: true,
          sessionInfo: data.sessionInfo,
          error: null,
        });
        // Show reconnect notification if agent was auto-started (e.g., after backend restart)
        if (data.wasAutoStarted) {
          console.log('[AgentChat] Agent was auto-started after backend restart:', agentId);
          addMessage(agentId, {
            type: 'system_response',
            responseType: 'auto-reconnect',
            content: 'Session reconnected - Agent was automatically restarted after backend restart.',
            timestamp: Date.now(),
          });
        }
        // Load history when observation starts (especially on reconnect/page reload)
        if (loadHistoryRef.current) {
          loadHistoryRef.current(agentId);
        }
        break;

      case WS_MESSAGE_TYPES.OBSERVATION_STOPPED:
        updateAgentSession(agentId, {
          active: false,
          isProcessing: false,
        });
        observedAgents.current.delete(agentId);
        break;

      case WS_MESSAGE_TYPES.AGENT_OUTPUT:
        // Parse JSON stream output from Claude
        if (data.content) {
          try {
            // Try to parse as JSON event
            const jsonEvent = JSON.parse(data.content);
            handleClaudeEvent(agentId, jsonEvent);
          } catch {
            // Raw text output - treat as assistant message
            if (getCurrentAssistantMessage(agentId) === undefined) {
              setCurrentAssistantMessage(agentId, data.content);
              addMessage(agentId, { type: 'assistant', content: data.content });
            } else {
              const current = getCurrentAssistantMessage(agentId) + data.content;
              setCurrentAssistantMessage(agentId, current);
              updateLastAssistantMessage(agentId, current);
            }
          }
        }
        break;

      case WS_MESSAGE_TYPES.AGENT_ERROR:
        updateAgentSession(agentId, {
          error: data.content,
          isProcessing: false,
        });
        addMessage(agentId, { type: 'error', content: data.content });
        break;

      case WS_MESSAGE_TYPES.AGENT_STATUS:
        updateAgentSession(agentId, {
          sessionInfo: data.sessionInfo,
        });
        break;

      case WS_MESSAGE_TYPES.PERMISSION_REQUEST:
        updateAgentSession(agentId, {
          pendingPermission: {
            requestId: data.requestId,
            tool: data.tool,
            description: data.description,
            timestamp: data.timestamp,
          },
        });
        break;

      case WS_MESSAGE_TYPES.CONTROL_INITIALIZED:
        updateAgentSession(agentId, {
          controlInitialized: true,
          availableCommands: data.commands || [],
        });
        break;

      case WS_MESSAGE_TYPES.PERMISSION_MODE_CHANGED:
        updateAgentSession(agentId, { permissionMode: data.mode });
        break;

      case WS_MESSAGE_TYPES.MODEL_CHANGED:
        updateAgentSession(agentId, { currentModel: data.model });
        break;

      case WS_MESSAGE_TYPES.ERROR:
        updateAgentSession(agentId, {
          error: data.message,
          isProcessing: false,
        });
        // If agent has no active session, remove from observed agents
        // This happens when trying to reconnect to an agent that was stopped
        if (data.code === 'E404' || (data.message && data.message.includes('no active session'))) {
          console.log(`[AgentChat] Agent ${agentId} has no active session, removing from observed agents`);
          observedAgents.current.delete(agentId);
          // Update session state to show it's inactive
          updateAgentSession(agentId, {
            active: false,
            connected: false,
          });
        }
        break;

      case 'assistant_message':
        if (data.partial) {
          if (getCurrentAssistantMessage(agentId) === undefined) {
            setCurrentAssistantMessage(agentId, '');
            addMessage(agentId, { type: 'assistant', content: '' });
          }
          setCurrentAssistantMessage(agentId, data.content);
          updateLastAssistantMessage(agentId, data.content);
        } else {
          if (getCurrentAssistantMessage(agentId) !== undefined) {
            updateLastAssistantMessage(agentId, data.content);
            setCurrentAssistantMessage(agentId, undefined);
          } else {
            addMessage(agentId, { type: 'assistant', content: data.content });
          }
        }
        break;

      case 'user_message':
        addMessage(agentId, {
          type: 'user',
          content: data.content,
          attachments: data.attachments,
        });
        break;

      case 'tool_use':
        addMessage(agentId, {
          type: 'tool_use',
          tool: data.tool,
          input: data.input,
          toolUseId: data.toolUseId,
        });
        break;

      case 'tool_result':
        addMessage(agentId, {
          type: 'tool_result',
          content: data.content,
          toolUseId: data.toolUseId,
          isError: data.isError,
        });
        break;

      case 'processing':
        updateAgentSession(agentId, { isProcessing: data.isProcessing });
        break;

      case 'result':
        handleClaudeEvent(agentId, data);
        break;

      case 'claude_event':
        handleClaudeEvent(agentId, data.event);
        break;

      case 'generation_stopped':
        updateAgentSession(agentId, { isProcessing: false });
        if (data.message) {
          addMessage(agentId, { type: 'system', content: data.message });
        }
        break;

      case 'session_resumed':
        // Session was restarted after stop (SIGINT + restart)
        updateAgentSession(agentId, {
          sessionInfo: { sessionId: data.sessionId },
          isProcessing: false,
          error: null,
        });
        break;

      case 'session_error':
        updateAgentSession(agentId, {
          error: data.error,
          isProcessing: false,
        });
        addMessage(agentId, { type: 'error', content: data.error });
        break;

      case 'session_paused':
        // Stream unhealthy, session paused for background retry
        updateAgentSession(agentId, {
          status: 'paused',
          isProcessing: false,
        });
        addMessage(agentId, {
          type: 'system',
          content: `Session paused: ${data.reason || 'Stream unhealthy'}. Auto-retry active.`,
        });
        break;

      case 'session_recovered':
        // Session recovered after reconnect
        updateAgentSession(agentId, {
          status: 'running',
          error: null,
        });
        addMessage(agentId, {
          type: 'system',
          content: 'Session recovered - connection restored.',
        });
        break;

      case 'context_update':
        updateAgentSession(agentId, (session) => ({
          stats: {
            ...(session.stats || {}),
            tokensUsed: data.tokensUsed,
            contextWindow: data.contextWindow,
            contextLeftPercent: data.percentRemaining,
          },
        }));
        break;

      case 'compacting_started':
        updateAgentSession(agentId, (session) => ({
          stats: {
            ...(session.stats || {}),
            isCompacting: true,
          },
        }));
        addMessage(agentId, {
          type: 'system',
          content: 'Context compacting in progress...',
        });
        break;

      case 'compacting_finished':
        updateAgentSession(agentId, (session) => ({
          stats: {
            ...(session.stats || {}),
            isCompacting: false,
          },
        }));
        break;

      case 'compact_boundary':
        updateAgentSession(agentId, (session) => ({
          stats: {
            ...(session.stats || {}),
            isCompacting: false,
            tokensUsed: 0,
            contextLeftPercent: 100,
          },
        }));
        addMessage(agentId, {
          type: 'system',
          content: `Context compacted (${data.trigger}). Was at ${data.percentUsedBeforeCompact}% usage.`,
        });
        break;

      case WS_MESSAGE_TYPES.PONG:
        // Connection alive
        break;

      case 'observer_attached':
        // Update session with data from observer_attached including permissionMode
        updateAgentSession(agentId, {
          sessionId: data.sessionId,
          isInitialized: data.isInitialized,
          permissionMode: data.permissionMode || 'default',
          controlInitialized: data.isInitialized,
        });
        console.log(`[AgentChat] Observer attached with permissionMode: ${data.permissionMode}`);
        break;

      case 'observe_stopped':
        // Observer was stopped (e.g., agent stopped)
        console.log(`[AgentChat] Observer stopped for ${agentId}`);
        break;

      case 'system_response':
        // System responses (help, query, context-warning, credentials) - display as system message with the response content
        console.log(`[AgentChat] System response received:`, data.responseType, data.content?.substring(0, 100));
        addMessage(agentId, {
          type: 'system_response',
          responseType: data.responseType,
          queryType: data.queryType,
          content: data.content,
          timestamp: data.timestamp,
          // Context warning fields
          percentUsed: data.percentUsed,
          threshold: data.threshold,
          // Credentials fields
          attributes: data.attributes,
          errorCode: data.errorCode,
          errorMessage: data.errorMessage,
          count: data.count,
          scope: data.scope,
        });
        break;

      case 'chat_activity':
        // Chat message was sent by agent - display activity indicator
        console.log(`[AgentChat] Chat activity:`, data.channel, data.content?.substring(0, 50));
        addMessage(agentId, {
          type: 'chat_activity',
          chatTags: [{
            channel: data.channel,
            content: data.content,
            timestamp: data.timestamp,
          }],
          timestamp: data.timestamp,
        });
        break;

      case 'raw_output':
        // Raw output from Claude CLI - may contain insight-result or other system responses
        console.log(`[AgentChat] Raw output received:`, data.content?.substring(0, 200));
        try {
          const rawData = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
          // Check if this is a result with insight tags
          if (rawData.result && (rawData.result.includes('<insight-result') || rawData.result.includes('<insight-query'))) {
            console.log(`[AgentChat] Found insight tags in raw_output result:`, rawData.result);
            addMessage(agentId, {
              type: 'system_response',
              responseType: 'insight-query',
              content: rawData.result,
              timestamp: data.timestamp || Date.now(),
            });
          }
        } catch (e) {
          console.log(`[AgentChat] Could not parse raw_output:`, e.message);
        }
        break;

      default:
        console.log(`[AgentChat] Unhandled message type:`, data.type, data);
    }
  }, [
    addMessage,
    updateLastAssistantMessage,
    updateAgentSession,
    handleClaudeEvent,
    getCurrentAssistantMessage,
    setCurrentAssistantMessage,
  ]);

  // ----------------------------------------
  // WebSocket Connection
  // ----------------------------------------

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      const ws = new WebSocket(getWebSocketUrl());

      ws.onopen = () => {
        if (!isComponentMounted.current) {
          ws.close();
          return;
        }

        console.log('[AgentChat] WebSocket connected');
        setWsConnected(true);

        // Start ping interval
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: WS_MESSAGE_TYPES.PING }));
          }
        }, 30000);

        // NOTE: We no longer restore agents from localStorage here.
        // The UIContext loads agents from the WorkspaceSession (backend),
        // and the SessionsView will call observeAgent for each agent in tabOrder.
        // This ensures agents stay in their correct sessions after restart.
        //
        // The observedAgents Set is still used to track currently observed agents
        // during the session, but it's no longer used for restoration on page load.
        console.log('[AgentChat] WebSocket connected, waiting for UIContext to restore agents from WorkspaceSession');
      };

      ws.onmessage = handleWsMessage;

      ws.onerror = (error) => {
        console.error('[AgentChat] WebSocket error:', error);
      };

      ws.onclose = () => {
        if (!isComponentMounted.current) return;

        console.log('[AgentChat] WebSocket disconnected');
        setWsConnected(false);

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Mark all sessions as disconnected
        setAgentSessions(prev => {
          const updated = {};
          Object.keys(prev).forEach(agentId => {
            updated[agentId] = {
              ...prev[agentId],
              connected: false,
            };
          });
          return updated;
        });

        // Auto-reconnect after delay
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isComponentMounted.current) {
            connect();
          }
        }, 2000);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[AgentChat] Failed to connect:', error);
    }
  }, [handleWsMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // ----------------------------------------
  // Agent Session Actions
  // ----------------------------------------

  /**
   * Load message history for an agent
   */
  const loadHistory = useCallback(async (agentId) => {
    // Increment version before fetch - used to detect stale responses on session switch
    historyLoadVersionRef.current[agentId] = (historyLoadVersionRef.current[agentId] || 0) + 1;
    const requestVersion = historyLoadVersionRef.current[agentId];

    try {
      console.log(`[AgentChat] Loading history for ${agentId} (version ${requestVersion})...`);
      const response = await fetch(`/api/history/${agentId}`, {
        credentials: 'include',
      });

      console.log(`[AgentChat] History response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.warn(`[AgentChat] Failed to load history for ${agentId}: ${response.status} - ${errorText}`);
        return;
      }

      const result = await response.json();
      const messages = result.data?.messages || [];

      // Check if this response is still relevant (version hasn't changed during fetch)
      if (requestVersion !== historyLoadVersionRef.current[agentId]) {
        console.log(`[AgentChat] Ignoring stale history for ${agentId} (request: ${requestVersion}, current: ${historyLoadVersionRef.current[agentId]})`);
        return;
      }

      console.log(`[AgentChat] History result:`, { messageCount: messages.length, cached: result.data?.cached });

      if (messages.length > 0) {
        // Set messages (replaces any existing)
        setMessages(agentId, messages);
        console.log(`[AgentChat] Loaded ${messages.length} history messages for ${agentId}`);
      }
    } catch (err) {
      console.error(`[AgentChat] Failed to load history for ${agentId}:`, err);
    }
  }, [setMessages]);

  /**
   * Start observing an agent session
   */
  const observeAgent = useCallback(async (agentId) => {
    // Ensure connection
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connect();
    }

    // Create session state if needed
    if (!sessionsRef.current[agentId]) {
      setAgentSessions(prev => ({
        ...prev,
        [agentId]: createAgentSessionState(agentId),
      }));
    }

    // Add to observed set (no longer persisted - WorkspaceSession is source of truth)
    observedAgents.current.add(agentId);

    // Add to UI tabs
    addTab(agentId);

    // History is loaded in the OBSERVATION_STARTED handler (handleWsMessage)
    // to avoid race conditions with duplicate loadHistory calls.

    // Send observe message when connected
    const sendObserve = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: WS_MESSAGE_TYPES.OBSERVE_AGENT,
          agentId,
        }));
      }
    };

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      sendObserve();
    } else {
      // Wait for connection
      const checkConnection = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          clearInterval(checkConnection);
          sendObserve();
        }
      }, 100);
      setTimeout(() => clearInterval(checkConnection), 5000);
    }
  }, [connect, addTab, loadHistory]);

  /**
   * Stop observing an agent session
   */
  const stopObserving = useCallback((agentId) => {
    observedAgents.current.delete(agentId);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: WS_MESSAGE_TYPES.STOP_OBSERVE,
        agentId,
      }));
    }

    updateAgentSession(agentId, {
      connected: false,
      active: false,
    });
  }, [updateAgentSession]);

  /**
   * Upload files to the server for an agent
   * @returns {Promise<Array>} Uploaded file info with paths
   */
  const uploadFiles = useCallback(async (agentId, files) => {
    if (!files || files.length === 0) return [];

    const formData = new FormData();
    for (const fileData of files) {
      formData.append('files', fileData.file);
    }

    const response = await fetch(`/api/uploads/${agentId}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || 'Failed to upload files');
    }

    const result = await response.json();
    return result.data || [];
  }, []);

  /**
   * Send message to agent
   */
  const sendMessage = useCallback(async (agentId, message, attachments = []) => {
    const session = sessionsRef.current[agentId];
    if (!session?.active) return;

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    let finalMessage = message;
    let uploadedFiles = [];

    // Upload files if any
    if (attachments.length > 0) {
      try {
        uploadedFiles = await uploadFiles(agentId, attachments);

        // Build attachment prefix for Claude (like V1)
        if (uploadedFiles.length > 0) {
          const attachmentInfo = uploadedFiles.map(f =>
            `${f.path} (${f.mimeType})`
          ).join(', ');
          finalMessage = `[Attachments: ${attachmentInfo}]\n\n${message}`;
        }
      } catch (err) {
        console.error('Failed to upload files:', err);
        addMessage(agentId, {
          type: 'error',
          content: `Failed to upload files: ${err.message}`,
        });
        return;
      }
    }

    // Add user message to local state immediately
    addMessage(agentId, {
      type: 'user',
      content: message,
      attachments: uploadedFiles.length > 0 ? uploadedFiles.map(f => ({
        path: f.path,
        name: f.originalName,
        type: f.mimeType,
        isImage: f.isImage,
      })) : undefined,
    });

    // Update unread count if not focused
    const isFocused = agentId === focusedAgentId || splitSessions.includes(agentId);
    if (!isFocused) {
      updateAgentSession(agentId, (session) => ({
        unreadCount: (session.unreadCount || 0) + 1,
      }));
    }

    updateAgentSession(agentId, { isProcessing: true });

    wsRef.current.send(JSON.stringify({
      type: WS_MESSAGE_TYPES.AGENT_MESSAGE,
      agentId,
      message: finalMessage,
    }));
  }, [addMessage, updateAgentSession, focusedAgentId, splitSessions, uploadFiles]);

  /**
   * Stop generation for agent
   */
  const stopGeneration = useCallback((agentId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: WS_MESSAGE_TYPES.STOP_GENERATION,
        agentId,
      }));
    }
    updateAgentSession(agentId, { isProcessing: false });
  }, [updateAgentSession]);

  /**
   * Change permission mode for agent
   */
  const changePermissionMode = useCallback((agentId, mode) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: WS_MESSAGE_TYPES.SET_PERMISSION_MODE,
        agentId,
        mode,
      }));
    }
  }, []);

  /**
   * Change model for agent via /model stdin command
   */
  const changeModel = useCallback((agentId, model) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // Optimistic update
      updateAgentSession(agentId, { currentModel: model });
      wsRef.current.send(JSON.stringify({
        type: WS_MESSAGE_TYPES.SET_MODEL,
        agentId,
        model,
      }));
    }
  }, [updateAgentSession]);

  /**
   * Respond to permission request
   */
  const respondToPermission = useCallback((agentId, requestId, allow, scope = 'once') => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: WS_MESSAGE_TYPES.PERMISSION_RESPONSE,
        agentId,
        requestId,
        allow,
        scope,
      }));
    }
    updateAgentSession(agentId, { pendingPermission: null });
  }, [updateAgentSession]);

  /**
   * Mark agent session as read
   */
  const markAsRead = useCallback((agentId) => {
    updateAgentSession(agentId, { unreadCount: 0 });
  }, [updateAgentSession]);

  /**
   * Close agent session (remove from UI)
   */
  const closeAgentSession = useCallback((agentId) => {
    stopObserving(agentId);
    removeTab(agentId);
    removeSessionMessages(agentId);
    setAgentSessions(prev => {
      const { [agentId]: removed, ...rest } = prev;
      return rest;
    });
  }, [stopObserving, removeTab, removeSessionMessages]);

  // ----------------------------------------
  // Effects
  // ----------------------------------------

  // Keep loadHistoryRef updated for use in message handler
  useEffect(() => {
    loadHistoryRef.current = loadHistory;
  }, [loadHistory]);

  // Connect on mount
  useEffect(() => {
    isComponentMounted.current = true;
    connect();

    return () => {
      isComponentMounted.current = false;
      disconnect();
    };
  }, [connect, disconnect]);

  // ----------------------------------------
  // Context Value
  // ----------------------------------------

  const value = useMemo(() => ({
    // Sessions state
    agentSessions,
    sessions: agentSessions, // Alias for compatibility

    // Actions
    observeAgent,
    stopObserving,
    sendMessage,
    stopGeneration,
    changePermissionMode,
    changeModel,
    respondToPermission,
    markAsRead,
    closeAgentSession,
    updateSession: updateAgentSession,

    // Connection - MUST be reactive for SessionsView to restore agents on reconnect
    isConnected: wsConnected,
  }), [
    agentSessions,
    wsConnected,
    observeAgent,
    stopObserving,
    sendMessage,
    stopGeneration,
    changePermissionMode,
    changeModel,
    respondToPermission,
    markAsRead,
    closeAgentSession,
    updateAgentSession,
  ]);

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  );
}

export function useAgentChat() {
  const context = useContext(AgentChatContext);
  if (!context) {
    throw new Error('useAgentChat must be used within AgentChatProvider');
  }
  return context;
}

// Hook to get a specific agent session
export function useAgentSessionData(agentId) {
  const { agentSessions } = useAgentChat();
  return agentSessions[agentId] || null;
}

// Compatibility aliases
export const useSessions = useAgentChat;
export const useSessionData = useAgentSessionData;
